/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute } from '@angular/router';
import { AdcModel, ShuttleModel } from '../../../shared/models';
import { AdcsApiService, ShuttleApiService } from '../../../shared/http';
import { AdcsUtilsService, NamespaceService } from '../../../shared/services/utils';
import { BaseComponent } from '../../../../../core/components';
import { Component, TemplateRef, ViewChild } from '@angular/core';
import { ConfirmationModel, ErrorResponseModel } from '../../../../../core/models';
import {
    DrawerService,
    LanguageService,
    LoadingService,
    LogService,
    ModalService,
    NavigationService,
    NotifyService,
    RbacService,
    SocketService,
    UtilsService
} from '../../../../../core/services';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { VendorEnum } from '../../../shared/enums';
import { concatMap, delay, takeUntil } from 'rxjs/operators';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { nameValidation } from '../../../../../core/directives';

@Component({
    selector: 'app-shuttle',
    templateUrl: './adcs-shuttle.component.html',
    styleUrls: ['./adcs-shuttle.component.scss']
})

export class AdcsShuttleComponent extends BaseComponent {
    /**
     * Reference to the content of this component.
     */
    @ViewChild('actionDots', { static: true }) public actionDots: TemplateRef<any>;

    /**
     * Reference to the content of this component.
     */
    @ViewChild('ShuttleInfo', { static: true }) public template: TemplateRef<any>;

    /**
     * Content for the template to be passed to the drawer service.
     */
    public templateContent: ShuttleModel;

    /**
     * Table row data.
     */
    public tableRows: any[] = [];

    /**
     * Search term for searching the services table, also used with the search query parameter in the url.
     */
    public searchTerm: string | [] = '';

    /**
     * Namespace selected key.
     */
    public selectedNamespaceKey: number;

    /**
     * Store associated ADC rows for the modal.
     */
    public associatedAdcRows: AdcModel[] = [];

    /**
     * Modal shuttle.
     */
    public modalShuttle = '';

    /**
     * New Loadbalancer FormGroup
     */
    public adcForm: FormGroup;

    /**
     * Flag on loading state.
     */
    public isLoading = true;

    /**
     * Stored ADCs for socket service.
     */
    public storedShuttles: ShuttleModel[] = [];

    /**
     * List of search keys to ignore when filtering.
     */
    public ignoreSearchKeys: string[] = ['applianceId'];

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Stored ADCs for socket service.
     * @private
     */
    private storedAdcs: AdcModel[] = [];

    /**
     * Set active row so the row can be reset if needed.
     * @private
     */
    private activeRow: ShuttleModel;

    constructor(
        private adcUtilsService: AdcsUtilsService,
        private adcsApiService: AdcsApiService,
        private drawerService: DrawerService,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private namespaceService: NamespaceService,
        private navigationService: NavigationService,
        private notifyService: NotifyService,
        private socketService: SocketService,
        private rbacService: RbacService,
        private route: ActivatedRoute,
        private shuttleApiService: ShuttleApiService,
        private utilsService: UtilsService
    ) {
        super();

        this.selectedNamespaceKey = this.namespaceService.selectedNamespace.key;

        this.setUpPageData();
        this.setupSubscribers();
        this.setupSocketListeners();
        this.setupForm();
        this.registerButtons();
    }

    /**
     * Check if the user can both adopt a shuttle and add an ADC.
     */
    public canAdoptAndSubmit(): boolean {
        const lookup = this.permissionsLookup;

        return !lookup['adcs-shuttle-adopt'] && !lookup['adcs-shuttle-add-adc']
            && (this.adcForm.invalid || this.adcForm.pristine);
    }

    /**
     * Click handler for ADC.
     * @param row
     */
    public openDrawer(row: ShuttleModel): void {
        if ((row && Object.keys(row).length) && row !== this.templateContent) {
            this.setDrawerContent(row);
        } else {
            this.drawerService.close();
            const emptyData = setTimeout(() => {
                this.templateContent = null;
                clearInterval(emptyData);
            }, 500);
        }
    }

    /**
     * Get locked state icon.
     * @param locked
     */
    public getLockedStateIcon(locked: any) {
        return locked ? 'locked' : 'unlocked';
    }

    /**
     * Get image src based on running status.
     * @param row
     */
    public getRunningStatusSrc(row: any): string {
        let statusName: string;
        if (row.adoptionStatus !== 'pending') {
            switch (row.status) {
                case 'online':
                    statusName = 'connected';
                    break;
                case 'pending':
                    statusName = 'pending';
                    break;
                default:
                    statusName = 'disconnected';
            }
        } else {
            statusName = 'pending';
        }

        return `./assets/centralisedManagement/icons/running-status-${statusName}.svg`;
    }

    /**
     * Set colour for ADC status.
     * @param row
     */
    public getAdcStatusClassName(row: ShuttleModel): string {
        if (row.status === 'pending' || row.adoptionStatus === 'errorConnectionTimeout') {
            return 'pending';
        } else if (row.status !== 'offline') {
            return 'online';
        } else {
            return 'disconnected';
        }
    }

    /**
     * Get shuttle status name.
     * @param status
     */
    public getShuttleStatusName(status: string): string {
        const statuses = {
            waiting: 'Waiting',
            downloading: 'Downloading',
            downloaded: 'Downloaded',
            setup: 'Setup',
            setupComplete: 'Setup complete',
            adopted: 'Adopted'
        };

        return statuses[status];
    }

    /**
     * Get associated column message.
     * @param row
     */
    public getAssociatedMessage(row: ShuttleModel): string {
        let message: string;
        if (row.associatedAdcs?.length > 1) {
            message = `${row.associatedAdcs.length} ${this.languageService.translate('adc_shuttle_associated')}`;
        } else if (row.associatedAdcs?.length === 1) {
            message = `${row.associatedAdcs.length} ${this.languageService.translate('adc_shuttle_associate')}`;
        } else {
            message = this.languageService.translate('adc_shuttle_associated_none');
        }

        return message;
    }

    /**
     * Show associated modal.
     * @param row
     */
    public showAssociatedModal(row: ShuttleModel): void {
        this.associatedAdcRows = row.associatedAdcs;
        this.modalShuttle = row.label;
        this.modalService.openModalInParent('usageModal');
    }

    /**
     * Get shuttle values from form.
     */
    public getShuttleValueFromForm() {
        return this.adcForm.value.shuttle;
    }

    /**
     * Close confirm ADC credentials modal.
     */
    public cancelAdoption(): void {
        const shuttle = this.adcForm.get('shuttle').value;
        const resetIndex: number = this.tableRows.findIndex(item => item.id === shuttle.id);
        if (resetIndex !== -1) {
            this.tableRows[resetIndex].adoptionStatus = 'pending';
        }
        this.activeRow.adoptionStatus = 'pending';
        this.loadingService.loadingButtonEmitter.next(false);
        this.modalService.close('confirm-adc-credentials');
    }

    /**
     * Remove button is enabled.
     * @param row
     */
    public removeButtonEnabled(row: any): boolean {
        return this.permissionsLookup['adcs-shuttle-remove'] && (!row.associatedAdcs?.length && !row.locked);
    }

    /**
     * Submit both shuttle and ADC.
     */
    public submitShuttleAndAdc(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const formValues = this.adcForm.value;
            const shuttle = formValues.shuttle;
            const adcData: object = {
                label: 'Loadbalancer.org appliance',
                ipAddress: shuttle.ipAddress,
                port: VendorEnum.LBENTERPRISE ? '9443' : '443',
                apiIpAddress: shuttle.ipAddress,
                apiPort: VendorEnum.LBENTERPRISE ? '9000' : '443',
                username: formValues.username,
                password: formValues.password,
                sidecarID: shuttle.id,
                vendor: VendorEnum.LBENTERPRISE
            };

            this.shuttleApiService.adopt(shuttle.namespaceId, shuttle.id)
                .pipe(
                    delay(5000),
                    concatMap(() => this.adcsApiService.create(this.selectedNamespaceKey, adcData)),
                    takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.completeHelper();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('submitShuttleAndAdc: adoptADC', 'Error submitting data', error);
                        this.notifyService.error('Failed to adopt ADC. Please contact support or try again in a few minutes.');
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Adopt ADC.
     * @param row
     */
    public adoptShuttle(row: ShuttleModel): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.activeRow = row;
            row.adoptionStatus = 'adopting';
            this.notifyService.muteLocalNotifications();
            const namespaceId = row.namespaceId ? row.namespaceId : this.selectedNamespaceKey;
            this.shuttleApiService.adopt(namespaceId, row.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        const timerId = setTimeout(() => {
                            this.completeHelper();
                            clearTimeout(timerId);
                            this.notifyService.unmuteLocalNotifications();
                            //  This is to stop the adopt button being in an active state before the page is re-drawn.
                            const timer = setTimeout(() => {
                                this.activeRow = null;
                                this.loadingService.loadingButtonEmitter.next(false);
                                clearTimeout(timer);
                            }, 750);

                            observer.next();
                            observer.complete();
                        }, 1500); // The API is really quick to respond so the setUpPageData can miss the status change!
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('AdcsListComponent: adoptADC', 'Error submitting data', error);
                        this.notifyService.error('Failed to adopt ADC. Please contact support or try again in a few minutes.');
                        this.notifyService.unmuteLocalNotifications();
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Confirm remove ADC.
     * @param row
     */
    public confirmRemoveShuttle(row: ShuttleModel): void {
        if (!this.permissionsLookup['adcs-shuttle-remove']) {
            return;
        }

        const confirmationObject: ConfirmationModel = {
            title: 'adc_shuttle_remove',
            message: 'adc_shuttle_remove_message',
            callBack: this.removeShuttle.bind(this, row),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Complete helper.
     * @private
     */
    private completeHelper(): void {
        this.modalService.close('confirm-adc-credentials');
        this.searchTerm = [];
        this.setUpPageData();
    }

    /**
     * Confirm Adc Credentials.
     * @private
     */
    // TODO - This has been temporarily removed as there is an ongoing issue with the backend service
    private confirmAdcCredentials(row: ShuttleModel): void {
        this.adcForm.get('shuttle').setValue(row);
        this.modalService.openModalInParent('confirm-adc-credentials');
    }

    /**
     * Remove shuttle from organisation.
     * @param row
     * @private
     */
    private removeShuttle(row: ShuttleModel): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.shuttleApiService.delete(this.selectedNamespaceKey, row.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        if (row.id === this.templateContent?.id) {
                            this.drawerService.close();
                        }
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('AdcsShuttleComponent: removeShuttle', 'Error deleting shuttle', error);
                        observer.error(error);
                        observer.complete();
                    },
                    complete: (): void => {
                        this.loadingService.loadingActionDotsEmitter.next(false);
                    }
                });
        });
    }

    /**
     * Set up page data with API response.
     * @private
     */
    private setUpPageData(): void {
        forkJoin({
            shuttles: this.shuttleApiService.getShuttleList(this.selectedNamespaceKey),
            adcs: this.adcsApiService.getAll(this.selectedNamespaceKey)
        })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.storedShuttles = data.shuttles;
                    this.storedAdcs = data.adcs;
                    this.populateTableRows(data.shuttles, data.adcs);
                    this.adcUtilsService.checkLocalStorageForDrawerContent(this.tableRows, this.setDrawerContent.bind(this));
                    this.isLoading = false;
                    this.checkForSearchQuery();
                },
                error: (error: ErrorResponseModel): void => {
                    this.isLoading = false;
                    this.logService.error('AdcsShuttleComponent:setUpPageData', 'Error getting Shuttles and ADCs', error);
                }
            });
    }

    /**
     * Populate table rows.
     * @param sidecars
     * @param adcs
     * @private
     */
    private populateTableRows(sidecars: any, adcs: any[]): void {
        this.tableRows = [];
        if (sidecars && sidecars.length) {
            const namespaces: any[] = this.namespaceService.namespaces;
            const sortedSidecars = this.utilsService.sortArrayByProperty(sidecars, 'legacy', [false, true]);
            for (const sidecar of sortedSidecars) {
                const associatedAdcs: AdcModel[] = adcs.filter(item => item.sidecarID === sidecar.id);
                const namespace = sidecar.namespaceID || sidecar.namespaceId || 0;
                this.tableRows.push({
                    associatedAdcs,
                    status: sidecar.status,
                    adoptionStatus: sidecar.adoptionStatus,
                    applianceId: sidecar.applianceID || sidecar.applianceId,
                    id: sidecar.id.toString(),
                    ipAddress: sidecar.ipAddress,
                    label: sidecar.label,
                    legacy: sidecar.legacy,
                    locked: sidecar.locked,
                    namespace: this.adcUtilsService.getNamespaceFriendlyName(namespaces, namespace),
                    namespaceId: sidecar.namespaceID || sidecar.namespaceId,
                    notes: sidecar.notes,
                    orgId: sidecar.orgID || sidecar.orgId,
                    port: sidecar.port,
                    runningStatus: sidecar.status,
                    subnets: this.filterSubnets(sidecar.subnets),
                    tags: sidecar.tags,
                    updateStatus: sidecar.updateStatus,
                    updateVersion: sidecar.updateVersion,
                    vendor: sidecar.vendor,
                    vendorIcon: this.adcUtilsService.getVendorIconName(sidecar.vendor),
                    vendorName: this.adcUtilsService.getVendorTitle(sidecar.vendor, 'Shuttle'),
                    version: sidecar.version,
                    warpEnabled: sidecar.warpEnabled,
                });
            }

            if (this.templateContent) {
                this.templateContent = this.tableRows.find(item => item.id === this.templateContent.id);
            }
        }
    }

    /**
     * Filter subnet to remove local gateways.
     * @param subnets
     * @private
     */
    private filterSubnets(subnets: any[]): any[] {
        return subnets?.filter(item => item.gateway !== '127.0.0.1' && item.gateway !== '::1');

    }

    /**
     * Check for search query.
     * @private
     */
    private checkForSearchQuery(): void {
        const query = this.utilsService.getObjectValue(this.route, 'snapshot.queryParamMap.params.sFilter');
        if (query) {
            this.searchTerm = query;
            this.navigationService.removeParamsFromUrl();
        }
    }

    /**
     * Setup page subscribers.
     * @private
     */
    private setupSubscribers(): void {
        this.namespaceService.selectedEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (namespace: any): void => {
                    this.selectedNamespaceKey = namespace.key;
                    this.setUpPageData();
                },
                error: (error: any): void => {
                    this.logService.error('AdcsListComponent: setupSubscribers', 'Unable to get selected namespace', error);
                }
            });
    }

    /**
     * Setup socket listeners.
     * @private
     */
    private setupSocketListeners(): void {
        this.socketService.on('sidecarAdd')
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    this.storedShuttles.push(response.data);
                    this.populateTableRows(this.storedShuttles, this.storedAdcs);
                }
            });

        this.socketService.on('sidecarRemove')
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    const removeIndex: number = this.storedShuttles.findIndex(item => item.id === response.data.id);
                    if (removeIndex !== -1) {
                        this.storedShuttles.splice(removeIndex, 1);
                        this.populateTableRows(this.storedShuttles, this.storedAdcs);
                    }
                }
            });

        this.socketService.on('sidecarStatusChange')
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    if (this.utilsService.getObjectValue(response, 'data.id')) {
                        this.updatePageOnADCStatusChange(response);
                    }
                }
            });
    }

    /**
     * Update the page on ADC status change.
     * @param response
     * @private
     */
    private updatePageOnADCStatusChange(response: { data: ShuttleModel }): void {
        const shuttleIndex: number = this.storedShuttles.findIndex((item: ShuttleModel): boolean => item.id === response.data.id);

        if (shuttleIndex !== -1 && this.adcUtilsService.dataHasChanged(this.storedShuttles[shuttleIndex], response.data)) {
            this.storedShuttles[shuttleIndex] = response.data;
            const clonedTableRows = this.utilsService.duplicateArray(this.storedShuttles);
            clonedTableRows[shuttleIndex] = response.data;
            this.populateTableRows(clonedTableRows, this.storedAdcs);

            const newContent = this.adcUtilsService.updateTemplateContent(this.templateContent, response, this.tableRows);
            if (newContent) {
                this.templateContent = newContent;
            }
        }
    }

    /**
     * Set drawer content.
     * @param row
     * @private
     */
    private setDrawerContent(row: any): void {
        this.templateContent = row;
        this.drawerService.open({
            title: row.label,
            actionDots: row.adoptionStatus === 'adopted' ? this.actionDots : null,
            content: null,
            template: this.template,
            data: row
        });
    }

    /**
     * Set up the adopt load balancer form for the modal.
     * @private
     */
    private setupForm(): void {
        this.adcForm = this.formBuilder.group({
            username: ['loadbalancer', [Validators.required, nameValidation]],
            password: [null, Validators.required],
            shuttle: [null]
        });
    }

    /**
     * Register the button permissions for the page.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'adcs-shuttle-add-shuttle': [
                { path: '/api/v1/:namespace/adcs/', methods: ['GET'] },
                { path: '/api/v1/:namespace/sidecars/', methods: ['GET'] },
                { path: '/api/v1/:namespace/sidecars/:id', methods: ['PATCH'] },
                { path: '/api/v1/:namespace/sidecars/:id/adopt', methods: ['PATCH'] },
                { path: '/api/v1/organisations/:id', methods: ['GET'] },
            ],
            'adcs-shuttle-add-adc': [
                { path: '/api/v1/:namespace/adcs/', methods: ['GET', 'POST'] }
            ],
            'adcs-shuttle-adopt': [
                { path: '/api/v1/:namespace/sidecars/:id/adopt', methods: ['PATCH'] },
            ],
            'adcs-shuttle-remove': [
                { path: '/api/v1/:namespace/sidecars/:id', methods: ['DELETE'] },
            ],
            'adcs-shuttle-edit': [
                { path: '/api/v1/:namespace/sidecars/:id', methods: ['GET', 'PATCH'] }
            ]
        };

        // TODO: Currently no function for updating a shuttle. When we have one the appropriate permissions
        // TODO: will need to be added in for 'adcs-shuttle-update'.

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
